import type { ReactNode } from "react"

import { CloseButton } from "@/components/ui/close-button"
import { CheckIcon, LightningIcon, SparkleIcon } from "@/components/ui/icons"
import { Text } from "@/components/ui/text"
import { tv, type VariantProps } from "@/lib/utils/tv"

const alert = tv({
  slots: {
    root: "rounded-6x flex items-start gap-300 border p-400",
    iconWrap: "flex size-8 shrink-0 items-center justify-center rounded-full",
    title: "font-display text-body text-fg font-semibold",
    body: "text-body-sm text-fg-subtle mt-50",
  },
  variants: {
    tone: {
      info: { root: "border-line-brand/30 bg-surface-brand-subtle", iconWrap: "bg-brand-muted text-fg-brand" },
      success: { root: "border-surface-success/30 bg-surface-success-subtle", iconWrap: "bg-surface-success text-fg-inverted" },
      warning: { root: "border-surface-warning/30 bg-surface-warning-subtle", iconWrap: "bg-surface-warning text-fg-inverted" },
      danger: { root: "border-error-text/30 bg-error-fill", iconWrap: "bg-surface-danger text-fg-inverted", title: "text-error-text", body: "text-error-text/80" },
    },
  },
  defaultVariants: { tone: "info" },
})

const DEFAULT_ICON = {
  info: <SparkleIcon size={14} />,
  success: <CheckIcon size={16} />,
  warning: <LightningIcon size={14} />,
  danger: (
    <Text as="span" variant="body" tone="inherit" className="font-bold">
      !
    </Text>
  ),
} as const

interface AlertProps extends VariantProps<typeof alert> {
  title: ReactNode
  children?: ReactNode
  icon?: ReactNode
  onClose?: () => void
  className?: string
}

export const Alert = ({ tone = "info", title, children, icon, onClose, className }: AlertProps) => {
  const styles = alert({ tone })
  return (
    <div role={tone === "danger" || tone === "warning" ? "alert" : "status"} className={styles.root({ className })}>
      <span className={styles.iconWrap()}>{icon ?? DEFAULT_ICON[tone]}</span>
      <div className="min-w-0 flex-1">
        <div className={styles.title()}>{title}</div>
        {children && <div className={styles.body()}>{children}</div>}
      </div>
      {onClose && <CloseButton onClick={onClose} />}
    </div>
  )
}
